import Backdrop from "@components/Backdrop"
import { StyledButton } from "@components/buttons/Button"
import { Add, Minus } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body16BoldStyles,
  body16MainStyles,
  smallcapsBold13Styles,
} from "@constants/typography"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { CartItem } from "@redux/types"
import React from "react"
import styled, { css } from "styled-components"

const openProps = css`
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`

const Container = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 362px;
  padding: 68px 20px 0;
  opacity: 0;
  transform: translateY(32px);
  transition:
    opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;

  ${({ isOpen }) => (isOpen ? openProps : "")}

  @media ${breakpoints.tablet} {
    padding: 68px 32px 0;
  }

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const StyledBackdrop = styled(Backdrop)`
  z-index: 3;
`

const Row = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
`

const Done = styled.button`
  ${body16MainStyles}
  flex-shrink: 0;
  padding: 0;
  outline: none;
  border: none;
  background-color: transparent;
  color: ${colors.mediumGray};

  &:focus,
  &:active {
    outline: none;
  }
`

const Product = styled.div`
  ${body16BoldStyles}
  padding-right: 24px;
`

const Buttons = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  margin-top: 44px;
`

const Button = styled(StyledButton)`
  justify-content: center;
  width: calc(50% - 10px);
  padding: 0;
  border-radius: 3px;
`

const QuantityWrapper = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: calc(50% - 10px);
  height: 48px;
  border-radius: 3px;
  background-color: ${colors.black};
`

const Quantity = styled.div`
  ${smallcapsBold13Styles}
  padding-top: 2px;
  color: ${colors.white};
`

const IconButton = styled.button<{
  disabled?: boolean
  padding?: string
}>`
  ${({ disabled, padding }) => css`
    align-items: center;
    display: flex;
    opacity: ${disabled ? "0.3" : "1"};
    width: calc((100% - 52px) / 2);
    height: 48px;
    padding: ${padding || "0 0 0 20px"};
    outline: none;
    border: none;
    background: none;
  `}
`

interface EditCartItemProps {
  containerRef: any
  delta: number
  isOpen: boolean
  onRemoveCartItem: () => void
  onUpdateCartItem: () => void
  product: { cartItem: CartItem; lineIndex: number } | undefined
  setDelta: React.Dispatch<React.SetStateAction<number>>
}

const EditCartItem: React.FC<EditCartItemProps> = ({
  containerRef,
  delta,
  isOpen,
  onRemoveCartItem,
  onUpdateCartItem,
  product,
  setDelta,
}) => {
  const modalRef: any = React.useRef(null)

  const incrementDelta = () => {
    setDelta(delta + 1)
  }

  const decrementDelta = () => {
    setDelta(delta - 1)
  }

  useModalBackgroundClose({
    isOpen,
    modalRef,
    onRequestClose: onUpdateCartItem,
  })

  return (
    <StyledBackdrop isOpen={isOpen} ref={containerRef}>
      <Container isOpen={isOpen} ref={modalRef}>
        <Row>
          {product?.cartItem && (
            <Product>{product.cartItem.productName}</Product>
          )}
          <Done onClick={onUpdateCartItem}>Done</Done>
        </Row>
        <Buttons>
          <Button onClick={onRemoveCartItem} white>
            Remove
          </Button>
          {product?.cartItem && (
            <QuantityWrapper>
              <IconButton
                disabled={product.cartItem.quantity + delta === 1}
                onClick={decrementDelta}
              >
                <Minus strokeColor={colors.white} />
              </IconButton>
              <Quantity>
                {delta === 0
                  ? product.cartItem.quantity
                  : product.cartItem.quantity + delta}
              </Quantity>
              <IconButton onClick={incrementDelta} padding="0 16px 0 0">
                <Add strokeColor={colors.white} />
              </IconButton>
            </QuantityWrapper>
          )}
        </Buttons>
      </Container>
    </StyledBackdrop>
  )
}

export default EditCartItem
